import { useRef, useState } from 'react'
import { useTheme, THEME_PREFERENCES, type ThemePreference } from '../../app/ThemeProvider'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { useToast } from '../../platform/feedback/ToastProvider'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { SegmentedControl } from '../../platform/primitives/SegmentedControl'
import { Switch } from '../../platform/primitives/Switch'
import { useLearnFromEditsState, useSetLearnFromEdits } from './useLearnFromEdits'
import { NotificationPreferences } from './NotificationPreferences'
import { ReportingCurrency } from './ReportingCurrency'
import './preferences.css'

/**
 * Settings › Preferences — M13. A per-browser theme row, then the account-level
 * behaviour: whether TrackE5 learns a category from the edits you make.
 *
 * **Starts at `<h2>`**: `ChromeLayout` owns the document's only `<h1>`.
 *
 * **ON needs no confirmation; OFF does**, because the server forgets everything
 * it learned and there is no undo (backend guide §5). Cancelling sends nothing.
 * The dialog's own failure text covers a failed OFF; a failed ON is toasted.
 */
const THEME_LABELS: Record<ThemePreference, string> = { system: 'System', light: 'Light', dark: 'Dark' }
const THEME_OPTIONS = THEME_PREFERENCES.map((value) => ({ value, label: THEME_LABELS[value] }))

export function Preferences() {
  const { preference, setPreference } = useTheme()
  const state = useLearnFromEditsState()
  const set = useSetLearnFromEdits()
  const { show } = useToast()
  const [confirming, setConfirming] = useState(false)
  const headingRef = useRef<HTMLHeadingElement>(null)

  const turnOn = () =>
    set.mutate(true, {
      onError: () =>
        show({ kind: 'error', message: 'We couldn’t save that. “Learn from my edits” is unchanged.' }),
    })

  return (
    <section className="preferences" aria-label="Appearance, currency and categorisation">
      <h2 className="preferences__title">Appearance</h2>
      <div className="preferences__row">
        <div className="preferences__text">
          <span className="preferences__name">Theme</span>
          <p className="preferences__help" id="theme-help">
            Saved on this browser only.
          </p>
        </div>
        <SegmentedControl
          label="Theme"
          name="theme"
          describedBy="theme-help"
          options={THEME_OPTIONS}
          value={preference}
          onChange={(next) => setPreference(next, 'settings')}
        />
      </div>
      <ReportingCurrency />
      <h2 className="preferences__title" id="preferences-title" tabIndex={-1} ref={headingRef}>
        Categorisation
      </h2>
      <Async
        state={state}
        surface="preferences"
        skeleton={<Skeleton />}
        empty={{ title: 'No preferences yet' }}
      >
        {(learn) => (
          <div className="preferences__row">
            <div className="preferences__text">
              <span className="preferences__name" id="learn-name">
                Learn from my edits
              </span>
              <p className="preferences__help" id="learn-help">
                When you change a transaction’s category, TrackE5 remembers it for that merchant and
                applies it automatically. Those rows are marked “Auto · from your edits”.
              </p>
            </div>
            <Switch
              label="Learn from my edits"
              describedBy="learn-help"
              checked={learn}
              onChange={(next) => (next ? turnOn() : setConfirming(true))}
            />
          </div>
        )}
      </Async>

      <NotificationPreferences />

      <ConfirmDialog
        open={confirming}
        mode="learn_from_edits_off"
        title="Turn off learning from your edits?"
        body="This forgets everything TrackE5 has learned from your edits so far. It can’t be undone: turning it back on starts with an empty memory."
        confirmLabel="Turn off and forget"
        pendingLabel="Turning off…"
        destructive
        onCancel={() => setConfirming(false)}
        onConfirm={async () => {
          await set.mutateAsync(false)
          setConfirming(false)
        }}
        errorFor={() => 'We couldn’t turn it off, so nothing was forgotten. Try again.'}
        restoreFocusTo={headingRef}
      />
    </section>
  )
}
